import {get, noop} from '@logistics-frontend/utils'
import clsx from 'clsx'
import React, {FocusEventHandler, useEffect, useMemo, useRef, useState} from 'react'

import {Grid} from '../grid'
import {Input, Trail} from '../input'
import {List, ListItem} from '../list'
import {Loader} from '../loader'
import {Paper} from '../paper'
import {Popper} from '../popper'
import {Typography} from '../typography'
import {classes, listClasses, listItemClasses} from './Suggest.classes'
import {SuggestProps, SuggestResponse, SuggestValue} from './types'
import {useTransition} from './useTransition'

// todo [ ] ОЧЕНЬ МНОГО НЕ ПОНЯТНОЙ ЛОГИКИ, ОТРЕФАКТОРИТЬ возможно унести в useAutocomplete <-------------

const initialHighlightedIndex = -1

const defaultRenderItem = (point: SuggestValue) => {
  return (
    <Grid alignItems="center" justify="space-between" container>
      <Grid item>
        <List.Label variant="body2">{point.title}</List.Label>
        <List.Description color="minor" variant="caption1">
          {point.subtitle}
        </List.Description>
      </Grid>
      <Grid item>
        <List.Description color="minor" variant="caption1">
          {point.distance}
        </List.Description>
      </Grid>
    </Grid>
  )
}

function Suggest<TValue>(props: SuggestProps<TValue>): React.ReactElement {
  const {
    fetchSuggest,
    onChange: onChangeProp = noop,
    onChangeError: onChangeErrorProp = noop,
    location,
    pointType,
    defaultText = '',
    point,
    error: errorProp,
    helperText: helperTextProp,
    trail: trailProp,
    resetInputOnBlur,
    onBlur,
    portal,
    loadingText,
    noItemsText,
    renderItem = defaultRenderItem,
    ...inputProps
  } = props
  const rootElementRef = useRef<HTMLDivElement | null>(null)
  const paperElementRef = useRef<HTMLDivElement | null>(null)
  const highlightedIndexRef = useRef<number>(initialHighlightedIndex)

  const [isOpened, setIsOpened] = useState(false)
  const [text, setText] = useState(defaultText)
  const [{isPending, payload, error}, startTransition] = useTransition<SuggestResponse<unknown, TValue>>(() => {
    return fetchSuggest({
      location,
      type: pointType,
      point,
      text,
    })
  })
  const {suggest = []} = payload || {}

  // если придет новый пропс defaultText
  // обновления в инпуте не произойдет, форсим
  useEffect(() => {
    if (text !== defaultText) {
      setText(defaultText)
    }
    // TODO я сходу тут во всем не разобрался, лучше придумать другой вариант
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultText])

  const fetchSuggestByPoint = (suggestValue: SuggestValue<TValue>): void => {
    setText(suggestValue.title)
    startTransition(
      () => {
        return fetchSuggest({
          location,
          type: pointType,
          __origin: suggestValue?.__origin,
        })
      },
      {
        onCommit({suggest, type}) {
          const point = type === 'final' ? suggest[0] : undefined

          if (point) {
            onChangeProp(point.__origin)
            setText(point.title)
            closeHandler()
          }
        },
        // @ts-expect-error тут не сильно важно совпадение типов
        onError: onChangeErrorProp,
      },
    )
  }

  const onItemChange = (suggestValue: SuggestValue<TValue> | undefined): void => {
    if (!suggestValue) {
      return
    }

    if (!get('__origin.address', suggestValue)) {
      fetchSuggestByPoint(suggestValue)
    } else {
      onChangeProp(suggestValue.__origin)
      setText(suggestValue.title)
      closeHandler()
    }
  }

  const listElementRef = (callback: (element: HTMLDivElement) => void) => {
    const paperElement = paperElementRef.current
    if (paperElement === null) {
      return
    }

    const listElement: HTMLDivElement | null = paperElement.querySelector(`.${listClasses.list}`)
    if (listElement === null) {
      return
    }

    callback(listElement)
  }
  const setHighlightedIndex = (index: number) => {
    // если индексы равны, ничего не делаем
    if (highlightedIndexRef.current === index) {
      return
    }

    highlightedIndexRef.current = index

    listElementRef(listElement => {
      listElement.querySelector('[data-focus]')?.removeAttribute('data-focus')
      listElement.querySelector(`[data-index="${index}"]`)?.setAttribute('data-focus', 'true')
    })
  }

  const inputKeyDownHandler: React.KeyboardEventHandler<HTMLDivElement> = event => {
    const currentIndex = highlightedIndexRef.current
    const length = suggest.length

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault()
        // не много computer science
        setHighlightedIndex((currentIndex + 1) % length)
        openHandler()
        break
      }
      case 'ArrowUp': {
        event.preventDefault()
        // не много computer science
        setHighlightedIndex((currentIndex - 1 + length) % length)
        openHandler()
        break
      }
      case 'Enter': {
        const point = suggest[currentIndex]

        if (point) {
          fetchSuggestByPoint(point)
        }

        break
      }
      case 'Escape': {
        closeHandler()
        break
      }
    }
  }

  const openHandler: VoidFunction = () => {
    setIsOpened(true)
  }
  const closeHandler: VoidFunction = () => {
    setHighlightedIndex(initialHighlightedIndex)
    setIsOpened(false)
  }

  const blurHandler: FocusEventHandler<HTMLInputElement> = event => {
    if (resetInputOnBlur) {
      setText(defaultText || '')
    }

    props.onBlur?.(event)
  }

  const suggestBlurHandler: FocusEventHandler<HTMLElement> = event => {
    const related = event.relatedTarget

    // Поскольку элементы списка тоже можно фокусировать, надо проверять что блюр произошел вне саджеста (и вне выпадашки)
    if (!rootElementRef.current?.contains(related) && !paperElementRef.current?.contains(related)) {
      closeHandler()
    }
  }

  const errorText = useMemo(() => (error?.body ? error?.body.message : error?.message), [error])

  return (
    <div
      ref={rootElementRef}
      className={classes.root}
      onBlur={suggestBlurHandler}
      onMouseDown={() => {
        openHandler()

        if (suggest.length > 1) {
          return
        }

        startTransition(() => {
          return fetchSuggest({
            location,
            type: pointType,
            point,
            text,
          })
        })
      }}
    >
      <Input
        autoCapitalize="none"
        autoComplete="__disable-autocomplete"
        error={!!errorText || errorProp}
        helperText={errorText || helperTextProp}
        onBlur={blurHandler}
        onChange={(inputValue: string) => {
          setHighlightedIndex(-1)
          setText(inputValue)
          openHandler()
          startTransition(
            () => {
              return fetchSuggest({location, text: inputValue, type: pointType})
            },
            {timeoutMs: 500},
          )
          onChangeProp(undefined, inputValue)
        }}
        onKeyDown={inputKeyDownHandler}
        spellCheck="false"
        trail={isPending ? <Loader /> : {content: trailProp, type: Trail}}
        value={text}
        {...inputProps}
      />
      <Popper
        className={classes.popup}
        opened={isOpened}
        portal={portal}
        role="presentation"
        style={rootElementRef.current ? {width: rootElementRef.current.clientWidth} : undefined}
        target={rootElementRef}
      >
        <Paper ref={paperElementRef} className={classes.paper}>
          {suggest.length > 0 ? (
            <List
              className={clsx(listClasses.root, listClasses.list)}
              getId={get('id')}
              items={suggest}
              onItemChange={onItemChange}
              renderItem={point => {
                const index = suggest.indexOf(point)

                return (
                  <div
                    className={listItemClasses.root}
                    data-index={index}
                    onMouseDown={event => event.preventDefault()}
                    // нужно для того, чтобы при клике не закрывался саджест
                    onMouseOver={() => setHighlightedIndex(index)}
                  >
                    {renderItem(point)}
                  </div>
                )
              }}
            />
          ) : (
            <ListItem Component={Grid} container>
              <Typography color="minor" variant="caption1">
                {isPending ? loadingText || 'Loading...' : noItemsText || 'Nothing found'}
              </Typography>
            </ListItem>
          )}
        </Paper>
      </Popper>
    </div>
  )
}

export default Suggest
